/**
 * @module pages/BrowsePage
 * @description The Choose a Cause page itself: hero → featured → browse →
 * trust (+ Match me and skeletons in Tier 2). Owns the browse query so the hero search and toolbar share it.
 */
import { useState, type JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { Browse } from "../components/Browse";
import { Featured } from "../components/Featured";
import { Hero } from "../components/Hero";
import { MatchMe } from "../components/MatchMe";
import { SkeletonBrowse } from "../components/Skeleton";
import { TrustSection } from "../components/Trust";
import { FEATURES } from "../config/tier";
import { EMPTY_QUERY } from "../domain/query";
import type { CauseQuery } from "../domain/types";

/** Tier 1/2 browse page. Shows a loading state until causes arrive. */
export function BrowsePage(): JSX.Element {
    const { causes } = useGiving();
    const [query, setQuery] = useState<CauseQuery>(EMPTY_QUERY);

    return (
        <>
            <Hero causeCount={causes?.length ?? null} onSearch={(text: string) => setQuery((q: CauseQuery) => ({ ...q, text }))} />
            {causes === null && FEATURES.motionSystem ? (
                <SkeletonBrowse />
            ) : causes === null ? (
                <div className="container" aria-busy="true" aria-live="polite">
                    <div className="spinner" />
                    <p className="visually-hidden">Loading causes…</p>
                </div>
            ) : (
                <>
                    <Featured causes={causes} />
                    {FEATURES.matchMe && <MatchMe causes={causes} />}
                    <Browse causes={causes} query={query} onQuery={setQuery} />
                </>
            )}
            <TrustSection />
        </>
    );
}
